import { MAX_PLAN_ENTRIES, MIN_REST_AMOUNT, PaymentPlanEntryStatus, PaymentPlanEntryTypes, } from '../../../payment-plans/index.js';
import { addInterval, appendPerEntryFeeVoucher, balanceAfterPlan, createNewNormalEntry, } from '../index.js';
import { createInstalmentCalculator } from './amount.js';
/* Accord (settlement) plan: entries amortize the agreed remaining sum rather
   than the balance — the debt left when the plan finishes is forgiven by the
   completion procedure. PENDING entries must always sum to the target, so
   entries are sized by plain arithmetic against the target instead of a
   projected-balance search. The config's method only decides how the target
   is cut into entries: fixed instalments (amount) or an even split
   (duration).

   The target is min(agreed remainder, projected debt): a plain plan shrinks
   with the balance by construction, but an accord is sized from the agreed
   sum, so when the debt falls below it (credit note, payment outside the
   plan, a total agreed above the debt) the plan would otherwise draw more
   than is owed. */
export const generateSettlement = async (input, config, settlementTotal) => {
    const { documents, economyPolicy, fixedDistributions, conditions } = input;
    // Seed entries — settled or still scheduled — already count toward the
    // agreed sum; only the remainder is scheduled.
    const seededAmount = input.plan.reduce((acc, e) => acc + (e.amount ?? 0), 0);
    const agreedRemainder = settlementTotal - seededAmount;
    if (agreedRemainder <= 0) {
        if (input.plan.length === 0) {
            throw new Error('The settlement total is already covered');
        }
        // Existing entries already cover the agreed sum — nothing to generate.
        // The completion check finishes the plan once they settle.
        return { plan: input.plan, filteredVouchers: input.filteredVouchers };
    }
    // The debt is projected to the plan's end date, since interest accrues
    // until then, so a draft sized against the agreed remainder pins down that
    // date. balanceAfterPlan counts the draft's entries as payments; adding them
    // back gives the debt the new entries have to cover.
    const draft = scheduleEntries(input, config, agreedRemainder);
    const draftBalance = await balanceAfterPlan(draft.filteredVouchers, documents, economyPolicy, fixedDistributions, draft.plan, conditions);
    const remainingDebt = agreedRemainder - draftBalance;
    const target = Math.min(agreedRemainder, remainingDebt);
    if (target <= 0) {
        throw new Error('Existing payments already exceed the total debt — cannot generate a payment plan');
    }
    return scheduleEntries(input, config, target);
};
const scheduleEntries = (input, config, target) => {
    const { reminderTimespan, paymentDeadlineTimespan, cancelTimespan } = input;
    let plan = input.plan;
    let filteredVouchers = input.filteredVouchers;
    let currDate = input.currDate;
    for (const entry of sizeEntries(config, input, target)) {
        if (entry.type === PaymentPlanEntryTypes.NORMAL) {
            // The plain paths' fee convention: NORMAL entries carry a per-entry
            // fee voucher, REST entries don't.
            filteredVouchers = appendPerEntryFeeVoucher(filteredVouchers, config.feeStructure, currDate.toISOString());
            plan = plan.concat([
                createNewNormalEntry(currDate, reminderTimespan, paymentDeadlineTimespan, cancelTimespan, entry.amount),
            ]);
        }
        else {
            plan = plan.concat([
                createNewRestEntry(currDate, reminderTimespan, paymentDeadlineTimespan, cancelTimespan, entry.amount),
            ]);
        }
        currDate = addInterval(config.interval, currDate);
    }
    return { plan, filteredVouchers };
};
const sizeEntries = (config, input, target) => {
    if (config.method === 'duration' && config.duration) {
        return evenSplitEntries(config.duration, target);
    }
    if (config.method === 'amount' && config.amount) {
        const { drawnAmountAt } = createInstalmentCalculator(config.amount, config.scheduledAmountChanges, config.feeStructure, input.recalculation);
        return instalmentEntries(config.interval, drawnAmountAt, input.currDate, target);
    }
    throw new Error('Please select either an amount or a duration');
};
// Fixed instalments until the leftover fits in a final REST entry.
const instalmentEntries = (interval, drawnAmountAt, startDate, target) => {
    const specs = [];
    let date = startDate.clone();
    let leftover = target;
    while (true) {
        const drawnAmount = drawnAmountAt(date);
        // The final entry absorbs the leftover — including a tail smaller than
        // the minimum rest — so the plan lands exactly on the target.
        if (leftover - drawnAmount < MIN_REST_AMOUNT ||
            specs.length >= MAX_PLAN_ENTRIES - 1) {
            specs.push({ amount: leftover, type: PaymentPlanEntryTypes.REST });
            return specs;
        }
        specs.push({ amount: drawnAmount, type: PaymentPlanEntryTypes.NORMAL });
        leftover -= drawnAmount;
        date = addInterval(interval, date);
    }
};
// The target split evenly over the requested number of entries.
const evenSplitEntries = (duration, target) => {
    if (duration <= 0) {
        throw new Error(`Duration cannot be less or equals 0`);
    }
    const baseAmount = Math.floor(target / duration);
    return Array.from({ length: duration }, (_, i) => ({
        // The last entry absorbs the rounding leftover so the entries sum
        // exactly to the target.
        amount: i === duration - 1 ? target - baseAmount * (duration - 1) : baseAmount,
        type: PaymentPlanEntryTypes.NORMAL,
    }));
};
const createNewRestEntry = (currDate, reminderTimespan, paymentDeadlineTimespan, cancelTimespan, amount) => ({
    reminder: currDate.clone().subtract(reminderTimespan, 'days').format(),
    date: currDate.clone().format(),
    deadline: currDate.clone().add(paymentDeadlineTimespan, 'days').format(),
    cancel: currDate.clone().add(cancelTimespan, 'days').format(),
    type: PaymentPlanEntryTypes.REST,
    amount,
    interestAmount: null,
    key: crypto.randomUUID(),
    status: PaymentPlanEntryStatus.PENDING,
});
//# sourceMappingURL=settlement.js.map